/**
 * Emulation Plugin Registry
 *
 * Central registry of all available emulation plugins. Add new plugins here
 * to make them available for sandbox configuration.
 *
 * @module
 */

import type { EmulationPlugin } from '../plugin.js';
import { emailPlugin } from './email.js';
import { googleOAuthPlugin } from './google-oauth.js';
import { jiraPlugin } from './jira.js';
import { slackPlugin } from './slack.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- type-erased registry for heterogeneous plugins
type AnyPlugin = EmulationPlugin<any, any, any>;

/** All registered emulation plugins, keyed by ID. */
const pluginRegistry = new Map<string, AnyPlugin>([
  [emailPlugin.id, emailPlugin],
  [googleOAuthPlugin.id, googleOAuthPlugin],
  [jiraPlugin.id, jiraPlugin],
  [slackPlugin.id, slackPlugin],
]);

/** Look up a plugin by ID. Returns undefined if not found. */
export function getPlugin(pluginId: string): AnyPlugin | undefined {
  return pluginRegistry.get(pluginId);
}

/** Get all registered plugin IDs. */
export function getPluginIds(): string[] {
  return Array.from(pluginRegistry.keys());
}

/** Get all registered plugins. */
export function getAllPlugins(): AnyPlugin[] {
  return Array.from(pluginRegistry.values());
}

/** Register a new plugin at runtime (for custom/third-party plugins). */
export function registerPlugin(plugin: AnyPlugin): void {
  pluginRegistry.set(plugin.id, plugin);
}

export { emailPlugin, emailFixtureSchema } from './email.js';
export type { CapturedEmail, EmailEmuConfig, EmailFixture, EmailState, EmailProvider } from './email.js';
export { googleOAuthPlugin } from './google-oauth.js';
export { jiraPlugin } from './jira.js';
export { slackPlugin, slackFixtureSchema } from './slack.js';
export type { SlackFixture, SlackState, PostedMessage } from './slack.js';
